import { useState, useEffect } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { Trash2, Plus } from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/clientWeb/components/ui/dialog"
import { Button } from "@/clientWeb/components/ui/button"
import { DatePicker } from "@/clientWeb/components/ui/date-picker"
import { TimePicker } from "@/clientWeb/components/ui/time-picker"
import { TimezoneSelect, getAllTimezones, getDefaultTimezone, formatTimezoneLabel } from "@/clientWeb/components/ui/timezone-select"
import { zonedTimeToUtcMs } from "@/shared/utils"

interface TZCard { uid: number; timezone: string }

/** Cards the planner will hold. Past this the "Add new time zone" control disables. */
export const MAX_TIMEZONES = 5

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  meetingDate?: string
  meetingTime?: string
  meetingTimezone?: string
  selectedTimezones: string[]
  onTimezonesChange: (timezones: string[]) => void
}

let _uid = 0
const nextUid = () => ++_uid

const dedupeZones = (zones: string[]): string[] => {
  const seen: string[] = []
  for (const tz of zones) {
    if (!seen.includes(tz)) seen.push(tz)
  }
  return seen
}

// Picks a zone not already on another card: the preferred zone, then the
// viewer's own zone, then London, and only then the first unused entry from
// the full IANA list (which on its own would look random). Candidates must be
// in that list, or the card's select would show nothing.
const pickUnusedZone = (used: string[], preferred: string): string => {
  const all = getAllTimezones()
  const own = Intl.DateTimeFormat().resolvedOptions().timeZone
  const known = (tz: string) => all.some(z => z.id === tz)
  const free = [preferred, own, "Europe/London"].find(tz => tz && known(tz) && !used.includes(tz))
    ?? all.find(tz => !used.includes(tz.id))?.id
  return free ?? preferred
}

const pad2 = (n: number) => String(n).padStart(2, "0")

const toDateString = (d: Date): string =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`

const parseDateString = (value: string): Date | undefined => {
  if (!value) return undefined
  const parts = value.split("-").map(Number)
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return undefined
  const [y, m, d] = parts
  return new Date(y, m - 1, d)
}

// `dateStr`/`timeStr` are wall-clock in `fromTz` (the meeting's own zone,
// not the browser's): that pair only means one instant once we know which
// zone it was picked in. Once resolved, the instant is formatted again in
// each card's own zone.
function getDisplayInTZ(dateStr: string, timeStr: string, fromTz: string, tz: string) {
  try {
    const [y, mo, d] = dateStr.split("-").map(Number)
    const [h, mi] = timeStr.split(":").map(Number)
    const ms = zonedTimeToUtcMs(y, mo, d, h, mi, fromTz)
    const date = new Date(ms).toLocaleDateString("en-US", { timeZone: tz, month: "long", day: "numeric", year: "numeric" })
    const time = new Date(ms).toLocaleTimeString("en-US", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: true })
    return { date, time }
  } catch {
    return { date: "-", time: "-" }
  }
}

export default function TimeZonePlannerDialog({ open, onOpenChange, meetingDate = "", meetingTime = "00:00", meetingTimezone = getDefaultTimezone(), selectedTimezones, onTimezonesChange }: Props) {
  const t = useTranslation()
  const makeInitial = (): TZCard[] => {
    const defaultTz = getDefaultTimezone()
    const tzs = selectedTimezones.length >= 2 ? selectedTimezones : [meetingTimezone || defaultTz, defaultTz]
    return dedupeZones(tzs).slice(0, MAX_TIMEZONES).map(tz => ({ uid: nextUid(), timezone: tz }))
  }

  // Preview-only state. Lets the user explore "what time would this be in X
  // if I scheduled it at Y?" without touching the parent meeting form. The
  // values seed from the meeting's date/time on each open so the planner
  // always reflects the latest form state when it pops up.
  const [previewDate, setPreviewDate] = useState<string>(meetingDate)
  const [previewTime, setPreviewTime] = useState<string>(meetingTime)
  const [cards, setCards] = useState<TZCard[]>(makeInitial)

  useEffect(() => {
    if (open) {
      setCards(makeInitial())
      setPreviewDate(meetingDate)
      setPreviewTime(meetingTime || "00:00")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const addCard = () => setCards(prev => {
    if (prev.length >= MAX_TIMEZONES) return prev
    const used = prev.map(c => c.timezone)
    const timezone = pickUnusedZone(used, meetingTimezone || getDefaultTimezone())
    return [...prev, { uid: nextUid(), timezone }]
  })
  const removeCard = (uid: number) => setCards(prev => prev.filter(c => c.uid !== uid))
  const updateTZ = (uid: number, tz: string) => setCards(prev => prev.map(c => c.uid === uid ? { ...c, timezone: tz } : c))
  const handleApply = () => { onTimezonesChange(dedupeZones(cards.map(c => c.timezone))); onOpenChange(false) }
  const handleCancel = () => { setCards(makeInitial()); onOpenChange(false) }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-full bg-card border border-border shadow-2xl rounded-2xl p-6 gap-0">
        <DialogHeader className="mb-4">
          <DialogTitle className="text-xl font-bold text-foreground">{t("meetings.timezonePlanner")}</DialogTitle>
          <DialogDescription>{t("meetings.timezonePlannerDesc")}</DialogDescription>
        </DialogHeader>

        {/* Editable preview controls: pick a hypothetical date/time and
            every card below recomputes its local rendition in real time.
            Both fields are wall-clock in the meeting's own zone, which is
            why the Time field spells that zone out: otherwise there is no
            way to tell which zone the typed time belongs to. */}
        <div className="my-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="relative">
            <span className="absolute -top-2.5 left-3 z-10 text-xs text-muted-foreground bg-card px-1">
              {t("meetings.schedule.date")}
            </span>
            <DatePicker
              date={parseDateString(previewDate)}
              setDate={(d) => setPreviewDate(d ? toDateString(d) : "")}
              placeholder={t("meetings.schedule.selectDate")}
              className="w-full"
            />
          </div>
          <div className="relative">
            <span className="absolute -top-2.5 left-3 z-10 text-xs text-muted-foreground bg-card px-1 truncate max-w-[calc(100%-0.5rem)]">
              {t("meetings.schedule.time")} · {formatTimezoneLabel(meetingTimezone)}
            </span>
            <TimePicker
              value={previewTime}
              onChange={(v) => setPreviewTime(v)}
              aria-label={t("meetings.schedule.time")}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-72 overflow-y-auto pr-1 mb-3">
          {cards.map(card => {
            const { date, time } = getDisplayInTZ(previewDate, previewTime, meetingTimezone, card.timezone)
            return (
              <div key={card.uid} className="border p-1 border-border rounded-xl overflow-hidden">
                <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border bg-muted/10">
                  <TimezoneSelect
                    value={card.timezone}
                    onChange={(tz) => updateTZ(card.uid, tz)}
                    variant="compact"
                    className="flex-1 text-xs"
                  />
                  <button onClick={() => removeCard(card.uid)} className="shrink-0 w-7 h-7 rounded-md bg-primary flex items-center justify-center hover:bg-primary/80 transition-colors">
                    <Trash2 className="w-3.5 h-3.5 text-white" />
                  </button>
                </div>
                <div className="p-3 bg-muted/10">
                  <p className="text-xs text-muted-foreground mb-1">{date}</p>
                  <p className="text-base font-bold text-foreground">{time}</p>
                </div>
              </div>
            )
          })}
        </div>

        <div className="flex justify-center items-center gap-2 mb-5">
          <button
            onClick={addCard}
            disabled={cards.length >= MAX_TIMEZONES}
            className={`text-sm font-medium flex items-center gap-1 transition-colors ${cards.length >= MAX_TIMEZONES ? "text-muted-foreground cursor-not-allowed" : "text-primary hover:text-primary/80"}`}
          >
            <Plus className="w-4 h-4" />
            {t("meetings.addNewTimezone")}
          </button>
          <span className="text-xs text-muted-foreground">{cards.length}/{MAX_TIMEZONES}</span>
        </div>

        <div className="flex justify-end gap-3 border-t border-border pt-4">
          <Button variant="outline" onClick={handleCancel} className="px-6">{t("common.cancel")}</Button>
          <Button onClick={handleApply} className="px-6">{t("meetings.apply")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
